import type { ComponentProps } from 'react';
import React, { useId, useState } from 'react';
import type { DistributedOmit } from 'type-fest';
import type { Placement } from '@floating-ui/react-dom';
import overflowMenuIcon from '../icons/non-prefilled/16x16/overflowMenu.svg';
import TutorialBeacon from '../onboarding/tutorial/TutorialBeacon';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import { IconButton } from './Button';
import Menu from './Menu';
import * as styles from './OverflowMenu.module.scss';
import Slot from './Slot';
import Tether, { parseOffsetValue } from './Tether';

type IconButtonSize = React.ComponentProps<typeof IconButton>['size'];

type OverflowMenuButtonProps = DistributedOmit<
  React.ComponentProps<typeof IconButton>,
  'aria-label' | 'children'
> & {
  size: IconButtonSize;
};

const OverflowMenuButton = React.forwardRef<
  HTMLElement,
  OverflowMenuButtonProps
>(({ size, ...props }: OverflowMenuButtonProps, ref) => (
  <IconButton ref={ref} {...props} aria-label="More options" size={size}>
    {overflowMenuIcon}
  </IconButton>
));

type OverflowMenuProps = Pick<
  ComponentProps<typeof Tether>,
  'placement' | 'offset' | 'portalContainer'
> & {
  beaconId?: string;
  beaconPlacement?: Placement;
  button?: React.ReactNode;
  children: React.ReactNode;
  buttonSize?: React.ComponentProps<typeof IconButton>['size'];
  onOpenChange?: (isOpen: boolean) => void;
  applyNegativeMargin?: boolean;
  ariaLabel?: string;
};

const DEFAULT_MAIN_AXIS_OFFSET = 4;

interface ControlledOverflowMenuProps extends OverflowMenuProps {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
  inline?: boolean;
}

const ControlledOverflowMenu = ({
  beaconId,
  beaconPlacement,
  button,
  children,
  buttonSize = 'xl',
  open,
  onOpenChange,
  portalContainer = document.body,
  applyNegativeMargin = true,
  offset: offsetValue,
  ariaLabel,
  placement = 'bottom-end',
}: ControlledOverflowMenuProps) => {
  const [hasBeenOpened, setHasBeenOpened] = useState(open);
  if (open && !hasBeenOpened) {
    setHasBeenOpened(true);
  }

  const {
    mainAxis: mainAxisOffset = DEFAULT_MAIN_AXIS_OFFSET,
    crossAxis: crossAxisOffset = 0,
  } = parseOffsetValue(offsetValue);

  const menuControlId = useId();

  const [anchorElement, setAnchorElement] = useState<Element | null>(null);

  const commonButtonProps = {
    ref: setAnchorElement,
    'aria-haspopup': true,
    'aria-expanded': open,
    'aria-controls': menuControlId,
    onClick: () => onOpenChange(!open),
  } as const;

  return (
    <ClickOutsideHandler
      onClickOutside={open ? () => onOpenChange(false) : undefined}
    >
      <Tether
        placement={placement}
        anchorElement={anchorElement}
        offset={{ mainAxis: mainAxisOffset, crossAxis: crossAxisOffset }}
        positionedView={
          hasBeenOpened && (
            <div
              className={styles.menuContainer}
              id={menuControlId}
              style={{ display: open ? undefined : 'none' }}
              onClick={() => onOpenChange(false)}
            >
              <Menu ariaLabel={ariaLabel}>{children}</Menu>
            </div>
          )
        }
        portalContainer={portalContainer}
      />
      {button ? (
        <div {...commonButtonProps}>{button}</div>
      ) : (
        <>
          {beaconId ? (
            <TutorialBeacon
              id={beaconId}
              hide={open}
              placement={beaconPlacement}
            >
              <Slot {...commonButtonProps}>
                <OverflowMenuButton
                  active={open}
                  size={buttonSize}
                  applyNegativeMargin={applyNegativeMargin}
                />
              </Slot>
            </TutorialBeacon>
          ) : (
            <OverflowMenuButton
              {...commonButtonProps}
              active={open}
              size={buttonSize}
              applyNegativeMargin={applyNegativeMargin}
            />
          )}
        </>
      )}
    </ClickOutsideHandler>
  );
};

interface UncontrolledOverflowMenuProps extends OverflowMenuProps {
  open?: undefined;
  onOpenChange?: undefined;
}

const UncontrolledOverflowMenu = (props: UncontrolledOverflowMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <ControlledOverflowMenu {...props} open={isOpen} onOpenChange={setIsOpen} />
  );
};

const OverflowMenu = (
  props: ControlledOverflowMenuProps | UncontrolledOverflowMenuProps,
) =>
  props.onOpenChange ? (
    <ControlledOverflowMenu {...props} />
  ) : (
    <UncontrolledOverflowMenu {...props} />
  );

export default OverflowMenu;
